import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { fetchSupplierPortfolio, fetchSupplierScores } from '../services/analyticsService';
import { novaGenerateReport, novaDownloadReport } from '../services/novaService';
import { NovaQuickAnalysisBar } from '../components/shared/NovaQuickAnalysis';
import ReportLanguageToggle, { useReportLanguage } from '../components/shared/ReportLanguageToggle';
import './Suppliers.css';

const CHART_CARD = { background: 'white', borderRadius: 14, border: '1px solid #e3e8ef', padding: '20px 16px', display: 'flex', flexDirection: 'column' };

function NovaInsight({ text }) {
  if (!text) return null;
  return (
    <div style={{ ...CHART_CARD, padding: '14px 18px', marginBottom: 20, flexDirection: 'row', alignItems: 'flex-start', gap: 10 }}>
      <span className="ai-badge" style={{ flexShrink: 0, marginTop: 1 }}>✦ Nova</span>
      <div style={{ fontSize: 13, color: 'var(--navy)', lineHeight: 1.55 }}>{text}</div>
    </div>
  );
}

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

export default function SupplierPortfolioEvolution() {
  const { instance: msalInstance } = useMsal();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ col: 'cumulative', dir: 'desc' });
  const [scoreMap, setScoreMap] = useState({});
  const [exportingPptx, setExportingPptx] = useState(false);
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const [exportErr, setExportErr] = useState('');
  const { lang: reportLang, setLang: setReportLang } = useReportLanguage();

  const loadList = useCallback(() => {
    setLoading(true); setErr('');
    fetchSupplierPortfolio(msalInstance)
      .then(d => setData(d))
      .catch(() => setErr('Failed to load supplier data'))
      .finally(() => setLoading(false));
  }, [msalInstance]);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    fetchSupplierScores(msalInstance)
      .then(res => {
        const m = {};
        for (const s of (res.suppliers || [])) m[s.supplier_name] = s.total_score;
        setScoreMap(m);
      })
      .catch(() => {});
  }, [msalInstance]);

  const loadDetail = useCallback((name) => {
    setSelectedSupplier(name);
    setDetailLoading(true);
    fetchSupplierPortfolio(msalInstance, { supplier: name })
      .then(d => setDetail(d))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  }, [msalInstance]);

  // Read ?supplier= on mount and load detail if present
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const sup = p.get('supplier');
    if (sup) loadDetail(sup);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync URL ↔ selectedSupplier
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (selectedSupplier) {
      if (p.get('supplier') !== selectedSupplier) navigate(`?supplier=${encodeURIComponent(selectedSupplier)}`, { replace: true });
    } else if (p.has('supplier')) {
      navigate(location.pathname, { replace: true });
    }
  }, [selectedSupplier, location.search, location.pathname, navigate]);

  const goBack = () => { setSelectedSupplier(null); setDetail(null); };

  const filtered = useMemo(() => {
    if (!data?.suppliers) return [];
    let arr = data.suppliers.map(r => ({ ...r, score: scoreMap[r.supplierName] ?? null }));
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      arr = arr.filter(r => r.supplierName.toLowerCase().includes(s));
    }
    const sorted = [...arr];
    sorted.sort((a, b) => {
      let av = a[sort.col], bv = b[sort.col];
      if (av == null) return 1; if (bv == null) return -1;
      if (typeof av === 'number') return sort.dir === 'desc' ? bv - av : av - bv;
      return sort.dir === 'desc' ? String(bv).localeCompare(String(av)) : String(av).localeCompare(String(bv));
    });
    return sorted;
  }, [data, search, sort, scoreMap]);

  const toggleSort = (col) => setSort(prev => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'desc' });

  const handleExport = useCallback(async (format) => {
    const setLoading = format === 'pptx' ? setExportingPptx : setExportingXlsx;
    setLoading(true); setExportErr('');
    try {
      const meta = await novaGenerateReport(msalInstance, { reportType: 'supplier-portfolio', format, filters: { supplier: selectedSupplier }, language: reportLang });
      const blob = await novaDownloadReport(msalInstance, meta.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = meta.filename || `supplier-portfolio.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { setExportErr('Export failed. Please try again.'); }
    finally { setLoading(false); }
  }, [msalInstance, selectedSupplier, reportLang]);

  const SortTh = ({ col, label }) => {
    const active = sort.col === col;
    return (
      <th className="sup-sort-head" onClick={() => toggleSort(col)}>
        <span style={{ cursor: 'pointer' }}>{label}{active && <span className="sup-sort-ico">{sort.dir === 'asc' ? '▲' : '▼'}</span>}</span>
      </th>
    );
  };

  // DETAIL VIEW
  if (selectedSupplier) {
    return (
      <div className="sup-page">
        <div className="mb-3">
          <h2 style={{ fontWeight: 700, fontSize: 20, color: 'var(--navy)', margin: 0 }}>Supplier portfolio evolution</h2>
          <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>FocusOne &middot; Supplier Analytics</div>
        </div>
        <button type="button" onClick={goBack} style={{ background: 'none', border: 'none', color: 'var(--teal)', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 4 }}>
          <i className="bi bi-arrow-left"></i> Back to list
        </button>

        {detailLoading && <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner-border spinner-border-sm me-2" role="status" />Loading...</div>}

        {!detailLoading && detail && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <h3 style={{ fontWeight: 700, fontSize: 18, color: 'var(--navy)', margin: 0 }}>{detail.supplier}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {exportErr && <span style={{ fontSize: 11, color: 'var(--red)' }}>{exportErr}</span>}
                <ReportLanguageToggle value={reportLang} onChange={setReportLang} compact />
                <button type="button" className="sup-badge teal-soft" disabled={exportingPptx} onClick={() => handleExport('pptx')} style={{
                  padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid #156082',
                  cursor: exportingPptx ? 'wait' : 'pointer', background: exportingPptx ? '#e8e8e8' : undefined,
                }}>
                  {exportingPptx ? 'Generating...' : 'Download PowerPoint'}
                </button>
                <button type="button" className="sup-badge teal-soft" disabled={exportingXlsx} onClick={() => handleExport('xlsx')} style={{
                  padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid #156082',
                  cursor: exportingXlsx ? 'wait' : 'pointer', background: exportingXlsx ? '#e8e8e8' : undefined,
                }}>
                  {exportingXlsx ? 'Generating...' : 'Download Excel'}
                </button>
              </div>
            </div>
            <NovaInsight text={detail.novaSummary} />

            {/* Growth chart */}
            {detail.timeline && detail.timeline.length > 0 && (
              <div style={{ ...CHART_CARD, marginBottom: 20 }}>
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: 15, color: 'var(--navy)', marginBottom: 12 }}>Portfolio Growth Over Time</div>
                <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={detail.timeline} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                      <XAxis dataKey="quarter" tick={{ fontSize: 10, fill: '#6c757d' }} interval={Math.max(0, Math.floor(detail.timeline.length / 12))} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#6c757d' }} width={40} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#6c757d' }} width={40} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line yAxisId="left" type="monotone" dataKey="cumulativeProducts" name="Products" stroke="#0D5C4F" strokeWidth={2} dot={false} />
                      <Line yAxisId="right" type="monotone" dataKey="moleculeBreadth" name="Molecules (INN)" stroke="#0B3D6B" strokeWidth={2} dot={false} />
                      <Line yAxisId="right" type="monotone" dataKey="atcAreas" name="ATC Areas" stroke="#3B1F6E" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* New ATC areas per quarter */}
            {detail.newAtcPerQuarter && detail.newAtcPerQuarter.length > 0 && (
              <div className="sup-table-wrap" style={{ marginBottom: 20 }}>
                <div style={{ padding: '10px 14px', fontWeight: 700, fontSize: 12, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                  New ATC Areas Entered
                </div>
                <div className="sup-table-scroll" style={{ maxHeight: '40vh' }}>
                  <table className="sup-table">
                    <thead><tr><th>Quarter</th><th>New Areas</th></tr></thead>
                    <tbody>
                      {detail.newAtcPerQuarter.map((r, i) => (
                        <tr key={i}>
                          <td>{r.quarter}</td>
                          <td>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                              {r.areas.map((a, j) => <span key={j} className="sup-badge teal-soft">{a}</span>)}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Timeline table */}
            <div className="sup-table-wrap">
              <div style={{ padding: '10px 14px', fontWeight: 700, fontSize: 12, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                Quarter-by-Quarter
              </div>
              <div className="sup-table-scroll" style={{ maxHeight: '40vh' }}>
                <table className="sup-table">
                  <thead><tr><th>Quarter</th><th>Net New</th><th>Cumulative</th><th>Molecules</th><th>ATC Areas</th></tr></thead>
                  <tbody>
                    {(detail.timeline || []).map((r, i) => (
                      <tr key={i}>
                        <td>{r.quarter}</td>
                        <td><span className="sup-badge teal-soft">+{r.netNew}</span></td>
                        <td><strong>{r.cumulativeProducts}</strong></td>
                        <td>{r.moleculeBreadth}</td>
                        <td>{r.atcAreas}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 8, fontStyle: 'italic' }}>Based on recorded product history.</div>
          </>
        )}

        {!detailLoading && !detail && (
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e3e8ef', padding: '40px 24px', textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--t3)' }}>Failed to load detail for {selectedSupplier}.</p>
          </div>
        )}
      </div>
    );
  }

  // LIST VIEW
  return (
    <div className="sup-page">
      <div className="mb-3">
        <h2 style={{ fontWeight: 700, fontSize: 20, color: 'var(--navy)', margin: 0 }}>Supplier portfolio evolution</h2>
        <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>FocusOne &middot; Supplier Analytics</div>
      </div>

      {data?.summary && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
          <div className="sup-kpi" style={{ flex: '1 1 140px', maxWidth: 200 }}>
            <div className="sup-kpi-lbl">Suppliers</div><div className="sup-kpi-val">{data.summary.totalSuppliers}</div>
          </div>
        </div>
      )}

      <NovaQuickAnalysisBar
        placeholder="Quick analysis — type a supplier name..."
        knownEntities={(data?.suppliers || []).map(s => s.supplierName)}
        entityLabel="supplier"
        reportType="supplier-portfolio"
        filterBuilder={(entity) => ({ supplier: entity })}
        onSubmit={(entity) => { setSearch(entity); loadDetail(entity); }}
      />

      <NovaInsight text={data?.novaSummary} />

      <div className="sup-filters-row">
        <div>
          <div className="sup-field-label" style={{ marginBottom: 2, fontSize: 11, color: 'var(--t2)' }}>Search supplier</div>
          <input className="sup-input" style={{ width: 220 }} placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {search && <button type="button" className="sup-clear-link" onClick={() => setSearch('')}>Clear</button>}
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner-border spinner-border-sm me-2" role="status" />Loading...</div>}
      {err && <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e3e8ef', padding: '40px 24px', textAlign: 'center' }}><p style={{ margin: 0, fontSize: 14, color: 'var(--red)' }}>{err}</p></div>}

      {!loading && !err && (
        <div className="sup-table-wrap">
          <div className="sup-table-scroll">
            <table className="sup-table">
              <thead>
                <tr>
                  <SortTh col="supplierName" label="Supplier" />
                  <SortTh col="score" label="Score" />
                  <SortTh col="cumulative" label="Products" />
                  <SortTh col="molecules" label="Molecules" />
                  <SortTh col="atcAreas" label="ATC Areas" />
                  <SortTh col="recentGrowth" label="Recent Growth" />
                  <th>First Seen</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 30, color: 'var(--t3)' }}>No suppliers found.</td></tr>
                ) : (
                  filtered.map((r, i) => (
                    <tr key={i} style={{ cursor: 'pointer' }} onClick={() => loadDetail(r.supplierName)}>
                      <td className="sup-inn" style={{ fontWeight: 600 }}>{r.supplierName}</td>
                      <td>
                        {r.score != null ? (
                          <span className="sup-badge" style={{
                            background: r.score >= 60 ? 'var(--teal-soft, #e6f4f0)' : r.score >= 35 ? '#fff8e1' : '#fce4ec',
                            color: r.score >= 60 ? 'var(--teal, #0D5C4F)' : r.score >= 35 ? '#f57f17' : '#c62828',
                            fontWeight: 700, fontSize: 12, padding: '2px 8px', borderRadius: 6,
                          }}>{r.score}</span>
                        ) : '—'}
                      </td>
                      <td><strong>{r.cumulative}</strong></td>
                      <td>{r.molecules}</td>
                      <td>{r.atcAreas}</td>
                      <td>{r.recentGrowth > 0 ? <span className="sup-badge teal-soft">+{r.recentGrowth}</span> : r.recentGrowth}</td>
                      <td>{fmtDate(r.firstSeen)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {filtered.length > 0 && (
            <div style={{ padding: '8px 14px', fontSize: 12, color: 'var(--t3)', borderTop: '1px solid #eef3f7' }}>
              {filtered.length} supplier{filtered.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}
      <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 8, fontStyle: 'italic' }}>Based on recorded product history.</div>
    </div>
  );
}
